import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Bike, Upload, X, Check, AlertCircle, Loader2,
  User, Gauge, Camera, Star, Edit, MapPin, Instagram, Home,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import Features from "@/components/landing/Features";
import { INDIAN_STATES, BIKE_BRANDS, citiesForState } from "@/data/formOptions";
import { api, INR } from "@/lib/marketplace";
import { useAuth } from "@/lib/auth";

const INSTAGRAM_URL = "https://www.instagram.com/bikerboulevard";
const YEARS = (() => {
  const current = new Date().getFullYear();
  const arr = [];
  for (let y = current; y >= 2012; y--) arr.push(String(y));
  return arr;
})();

const INITIAL = {
  seller_name: "", seller_phone: "", seller_whatsapp: "", seller_email: "",
  seller_city: "", seller_state: "", seller_locality: "",
  brand: "", model: "", variant: "",
  registration_number: "", registration_state: "", registration_city: "", registration_year: "",
  kms_driven: "", engine_cc: "", fuel_type: "Petrol", previous_owners: "1",
  has_loan: false, has_challan: false,
  expected_price: "", condition: "Good",
  accident_history: false, modifications: "", additional_notes: "",
};

// =========================
// Field components (shared)
// =========================
function FieldLabel({ children, required }) {
  return (
    <label className="block text-[13px] font-semibold text-zinc-300 mb-2">
      {children}
      {required && <span className="text-[#00cccc] ml-1">*</span>}
    </label>
  );
}
const INPUT_BASE = "w-full px-4 py-3.5 text-[15px] bg-[#101317] border rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00cccc]/50 focus:border-[#00cccc]/60 transition-all disabled:opacity-50";

function TextInput({ label, name, value, onChange, placeholder = "", type = "text", required, error, disabled, maxLength, autoComplete }) {
  return (
    <div>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete={autoComplete}
        data-testid={`input-${name}`}
        className={`${INPUT_BASE} ${error ? "border-red-500/60 focus:ring-red-500/40" : "border-white/10 hover:border-white/20"}`}
      />
      {error && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}
function SelectInput({ label, name, value, onChange, options, required, error, placeholder = "Select...", disabled }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        disabled={disabled}
        data-testid={`select-${name}`}
        className={`${INPUT_BASE} ${error ? "border-red-500/60" : "border-white/10 hover:border-white/20"}`}
        style={{ colorScheme: "dark" }}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => typeof o === "object"
          ? <option key={o.value} value={o.value} className="bg-[#15181C]">{o.label}</option>
          : <option key={o} value={o} className="bg-[#15181C]">{o}</option>)}
      </select>
      {error && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}
function RadioGroup({ label, name, value, onChange, options, required }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o;
          return (
            <button key={o} type="button" onClick={() => onChange(name, o)}
              data-testid={`radio-${name}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-2.5 rounded-xl text-[14px] font-medium border transition-all ${active
                ? "bg-[#00cccc]/15 border-[#00cccc] text-[#00cccc] shadow-[0_0_16px_rgba(0,204,204,0.25)]"
                : "bg-[#15181C] border-white/10 text-zinc-300 hover:border-white/20 hover:text-white"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function ToggleBool({ label, name, value, onChange }) {
  return (
    <RadioGroup label={label} name={name} value={value ? "Yes" : "No"}
      onChange={(n, v) => onChange(n, v === "Yes")} options={["No", "Yes"]} />
  );
}
function TextArea({ label, name, value, onChange, placeholder, rows = 4, maxLength }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <textarea value={value || ""} onChange={(e) => onChange(name, e.target.value)}
        rows={rows} placeholder={placeholder} maxLength={maxLength}
        data-testid={`textarea-${name}`}
        className={`${INPUT_BASE} resize-none border-white/10`} />
    </div>
  );
}
function SectionHeader({ n, icon: Icon, title }) {
  return (
    <div className="mb-6 flex items-start gap-4">
      <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center bg-[#00cccc]/10 border border-[#00cccc]/30">
        <Icon className="w-5 h-5 text-[#00cccc]" />
      </div>
      <div>
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Section {n}</div>
        <h2 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-white">{title}</h2>
      </div>
    </div>
  );
}

// =========================
// Validation
// =========================
function validateAll(f, photos) {
  const e = {};
  if (!f.seller_name?.trim() || f.seller_name.trim().length < 2) e.seller_name = "Enter your full name";
  if (!/^(\+91)?[6-9]\d{9}$/.test((f.seller_phone || "").replace(/\s+/g, ""))) e.seller_phone = "Enter a valid 10-digit mobile";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.seller_email || "")) e.seller_email = "Enter a valid email";
  if (!f.seller_city?.trim()) e.seller_city = "Select your city";
  if (!f.seller_state) e.seller_state = "Select your state";
  if (!f.brand) e.brand = "Select brand";
  if (!f.model?.trim()) e.model = "Enter model";
  if (!f.registration_number?.trim()) e.registration_number = "Enter registration number";
  if (!f.registration_state) e.registration_state = "Select registration state";
  if (!f.registration_city) e.registration_city = "Select registration city";
  if (!f.registration_year) e.registration_year = "Select year";
  if (!/^\d+$/.test(String(f.kms_driven).trim())) e.kms_driven = "Enter KMs driven";
  if (f.engine_cc && !/^\d+$/.test(String(f.engine_cc).trim())) e.engine_cc = "Invalid CC";
  if (!/^\d+$/.test(String(f.expected_price).trim())) e.expected_price = "Enter expected price";
  if (!photos.length) e._photos = "Upload at least one photo";
  return e;
}

// =========================
// Main page
// =========================
export default function ListMyBike() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [whatsappSame, setWhatsappSame] = useState(true);
  const [photos, setPhotos] = useState([]); // { file, preview, uploading, photo_id, error }
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [valuation, setValuation] = useState(null);
  const [valuating, setValuating] = useState(false);
  const [preview, setPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [created, setCreated] = useState(null);
  const [catalog, setCatalog] = useState({}); // brand -> [models]
  const [modelOther, setModelOther] = useState(false);
  const { user, loading: authLoading, login } = useAuth();

  useEffect(() => { document.title = "List My Bike — Biker Boulevard"; window.scrollTo(0, 0); }, []);

  // Pre-fill seller identity from the signed-in account (never overwrite typed values)
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      seller_name: f.seller_name || user.name || "",
      seller_email: f.seller_email || user.email || "",
    }));
  }, [user]);

  // Brand -> model catalog for the model dropdown
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/valuation/catalog`)
      .then((r) => r.json())
      .then((d) => setCatalog(d.brands || {}))
      .catch(() => {});
  }, []);

  const onChange = (name, value) => {
    if (name === "brand") setModelOther(false);
    if (name === "model" && value === "__other__") { setModelOther(true); value = ""; }
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "registration_number") next.registration_number = (value || "").toUpperCase();
      if (name === "registration_state") next.registration_city = "";
      if (name === "seller_state") next.seller_city = "";
      if (name === "brand") next.model = "";
      if (name === "seller_phone" && whatsappSame) next.seller_whatsapp = value;
      return next;
    });
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  // Live BB valuation
  const canValue = form.brand && form.model && form.registration_year && form.kms_driven;
  useEffect(() => {
    if (!canValue) { setValuation(null); return; }
    const t = setTimeout(async () => {
      setValuating(true);
      try {
        const v = await api.valuate({
          brand: form.brand, model: form.model,
          registration_year: parseInt(form.registration_year),
          kms_driven: parseInt(form.kms_driven) || 0,
          engine_cc: form.engine_cc ? parseInt(form.engine_cc) : undefined,
          previous_owners: parseInt(form.previous_owners) || 1,
          seller_city: form.seller_city, condition: form.condition,
          fuel_type: form.fuel_type, accident_history: !!form.accident_history,
        });
        setValuation(v);
      } catch { setValuation(null); }
      finally { setValuating(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.brand, form.model, form.registration_year, form.kms_driven, form.engine_cc,
      form.previous_owners, form.seller_city, form.condition, form.fuel_type, form.accident_history]);

  // Photos
  const onPhotoFiles = async (files) => {
    const arr = Array.from(files || []);
    const room = 5 - photos.length;
    if (room <= 0) return;
    const toAdd = arr.slice(0, room).filter((f) => /^image\//i.test(f.type) && f.size <= 5 * 1024 * 1024);
    const stubs = toAdd.map((file) => ({
      file, preview: URL.createObjectURL(file),
      uploading: true, photo_id: null, error: null,
    }));
    setPhotos((p) => [...p, ...stubs]);
    // Upload in parallel
    stubs.forEach(async (stub) => {
      try {
        const { photo_id } = await api.uploadPhoto(stub.file);
        setPhotos((p) => p.map((x) => x.file === stub.file ? { ...x, uploading: false, photo_id } : x));
      } catch (err) {
        setPhotos((p) => p.map((x) => x.file === stub.file ? { ...x, uploading: false, error: err.message } : x));
      }
    });
  };
  const removePhoto = (idx) => {
    setPhotos((p) => {
      const next = p.filter((_, i) => i !== idx);
      if (primaryIdx >= next.length) setPrimaryIdx(0);
      else if (idx < primaryIdx) setPrimaryIdx((v) => Math.max(0, v - 1));
      return next;
    });
  };
  const movePhoto = (idx, dir) => {
    setPhotos((p) => {
      const next = [...p];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return p;
      [next[idx], next[j]] = [next[j], next[idx]];
      // update primary index if involved
      if (primaryIdx === idx) setPrimaryIdx(j);
      else if (primaryIdx === j) setPrimaryIdx(idx);
      return next;
    });
  };

  const onPreview = () => {
    const v = validateAll(form, photos);
    setErrors(v);
    if (Object.keys(v).length) {
      requestAnimationFrame(() => {
        const first = document.querySelector('[class*="border-red-500"]');
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    if (photos.some((p) => p.uploading || !p.photo_id)) {
      setSubmitError("Please wait for photos to finish uploading");
      return;
    }
    setSubmitError(null);
    setPreview(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true); setSubmitError(null);
    try {
      const payload = {
        seller_name: form.seller_name,
        seller_phone: form.seller_phone,
        seller_whatsapp: whatsappSame ? form.seller_phone : form.seller_whatsapp,
        seller_email: form.seller_email,
        seller_city: form.seller_city,
        seller_state: form.seller_state,
        seller_locality: form.seller_locality || null,
        brand: form.brand, model: form.model, variant: form.variant || null,
        registration_number: form.registration_number,
        registration_state: form.registration_state,
        registration_city: form.registration_city,
        registration_year: parseInt(form.registration_year),
        kms_driven: parseInt(form.kms_driven),
        engine_cc: form.engine_cc ? parseInt(form.engine_cc) : null,
        fuel_type: form.fuel_type,
        previous_owners: parseInt(form.previous_owners) || 1,
        has_loan: !!form.has_loan,
        has_challan: !!form.has_challan,
        expected_price: parseInt(form.expected_price),
        condition: form.condition,
        accident_history: !!form.accident_history,
        modifications: form.modifications || null,
        additional_notes: form.additional_notes || null,
        photo_ids: photos.map((p) => p.photo_id).filter(Boolean),
        primary_photo_index: primaryIdx,
      };
      const res = await api.createListing(payload);
      setCreated(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err.message);
    } finally { setSubmitting(false); }
  };

  const primaryUrl = useMemo(
    () => photos[primaryIdx]?.preview || photos[0]?.preview || null,
    [photos, primaryIdx]
  );

  return (
    <main data-testid="list-my-bike-page" className="relative bg-[#0D0F12] text-white min-h-screen overflow-x-hidden">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 10%, rgba(0,204,204,0.08), transparent 60%), #0D0F12" }} />
        <div className="absolute inset-0 grid-bg opacity-10" />
      </div>

      <div className="pt-24 pb-24 max-w-[1000px] mx-auto px-5 md:px-8">
        {authLoading ? (
          <div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div>
        ) : !user ? (
          <div className="pt-16 max-w-md mx-auto text-center" data-testid="list-gate">
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Seller Access</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Sign in to list your bike</h2>
            <p className="mt-3 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Listing on the marketplace requires a Google account — it keeps listings accountable, links them to your dashboard, and lets buyers chat with you securely.
            </p>
            <button onClick={login} data-testid="list-gate-login"
              className="mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_36px_rgba(0,204,204,0.7)] transition-all">
              Continue with Google <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4 text-sm text-zinc-500">
              Prefer zero sign-in? <Link to="/sell-my-bike" className="text-[#00cccc] hover:underline">Sell directly to Biker Boulevard</Link> instead.
            </p>
          </div>
        ) : created ? (
          <SuccessScreen listingId={created.listing_id} navigate={navigate} />
        ) : preview ? (
          <PreviewScreen
            form={form} photos={photos} primaryUrl={primaryUrl} valuation={valuation}
            onEdit={() => setPreview(false)}
            onPublish={submit}
            submitting={submitting} submitError={submitError}
          />
        ) : (
          <FormScreen
            form={form} onChange={onChange} errors={errors}
            whatsappSame={whatsappSame} setWhatsappSame={(c) => {
              setWhatsappSame(c);
              if (c) setForm((f) => ({ ...f, seller_whatsapp: f.seller_phone }));
            }}
            photos={photos} onPhotoFiles={onPhotoFiles} removePhoto={removePhoto}
            movePhoto={movePhoto} primaryIdx={primaryIdx} setPrimaryIdx={setPrimaryIdx}
            valuation={valuation} valuating={valuating}
            onPreview={onPreview} submitError={submitError}
            catalog={catalog} modelOther={modelOther} setModelOther={setModelOther}
          />
        )}
      </div>
      <Features />
      <Footer />
    </main>
  );
}

// =========================
// Form screen
// =========================
function FormScreen({ form, onChange, errors, whatsappSame, setWhatsappSame, photos, onPhotoFiles, removePhoto, movePhoto, primaryIdx, setPrimaryIdx, valuation, valuating, onPreview, submitError, catalog, modelOther, setModelOther }) {
  return (
    <>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00cccc]/25 bg-[#00cccc]/5 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00cccc] animate-pulse-glow" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#00cccc] font-mono">// List My Bike</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight">
          List Your Motorcycle. <span className="text-gradient-cyan">Reach Serious Riders.</span>
        </h1>
        <p className="mt-3 text-zinc-400 text-base sm:text-lg max-w-2xl">
          Create a premium listing on the Biker Boulevard marketplace in minutes.
        </p>
      </div>

      <div className="rounded-2xl p-5 sm:p-8 space-y-10 border border-white/10" style={{ background: "#15181C" }}>
        <section>
          <SectionHeader n="01" icon={User} title="About You" />
          <div className="space-y-5">
            <TextInput label="Full Name" name="seller_name" value={form.seller_name} onChange={onChange} required error={errors.seller_name} placeholder="e.g. Rohit Sharma" autoComplete="name" />
            <div className="grid sm:grid-cols-2 gap-5">
              <TextInput label="Phone Number" name="seller_phone" value={form.seller_phone} onChange={onChange} required error={errors.seller_phone} placeholder="10-digit mobile" type="tel" maxLength={13} />
              <TextInput label="WhatsApp Number" name="seller_whatsapp" value={whatsappSame ? form.seller_phone : form.seller_whatsapp} onChange={onChange} placeholder="10-digit mobile" type="tel" disabled={whatsappSame} maxLength={13} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300" data-testid="whatsapp-same-toggle">
              <input type="checkbox" checked={whatsappSame} onChange={(e) => setWhatsappSame(e.target.checked)} className="w-4 h-4 accent-[#00cccc]" />
              WhatsApp number is the same as my phone number
            </label>
            <TextInput label="Email Address" name="seller_email" value={form.seller_email} onChange={onChange} required error={errors.seller_email} placeholder="you@example.com" type="email" />
            <div className="grid sm:grid-cols-3 gap-5">
              <SelectInput label="State" name="seller_state" value={form.seller_state} onChange={onChange} options={INDIAN_STATES} required error={errors.seller_state} placeholder="Select state" />
              <SelectInput label="City" name="seller_city" value={form.seller_city} onChange={onChange} options={citiesForState(form.seller_state)} required error={errors.seller_city} disabled={!form.seller_state} placeholder={form.seller_state ? "Select city" : "Select state first"} />
              <TextInput label="Locality / Area" name="seller_locality" value={form.seller_locality} onChange={onChange} placeholder="e.g. Gomti Nagar (optional)" />
            </div>
          </div>
        </section>

        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <section>
          <SectionHeader n="02" icon={Bike} title="Vehicle Details" />
          <p className="mt-2 mb-5 text-sm text-zinc-400 leading-relaxed" data-testid="condition-honesty-note">
            Be honest about your vehicle&apos;s condition — it directly affects the price our valuation engine estimates for your bike.
          </p>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <SelectInput label="Brand" name="brand" value={form.brand} onChange={onChange} options={BIKE_BRANDS} required error={errors.brand} placeholder="Select brand" />
              {catalog[form.brand] && !modelOther ? (
                <SelectInput label="Model" name="model" value={form.model} onChange={onChange}
                  options={[...catalog[form.brand], { value: "__other__", label: "Other (type manually)" }]}
                  required error={errors.model} placeholder="Select model" />
              ) : (
                <div>
                  <TextInput label="Model" name="model" value={form.model} onChange={onChange} required error={errors.model} placeholder="e.g. Duke 390" />
                  {catalog[form.brand] && (
                    <button type="button" onClick={() => setModelOther(false)} className="mt-1 text-[11px] text-[#00cccc] hover:underline" data-testid="model-list-link">
                      Choose from {form.brand} models
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <TextInput label="Variant" name="variant" value={form.variant} onChange={onChange} placeholder="e.g. Standard" />
              <TextInput label="Engine CC" name="engine_cc" value={form.engine_cc} onChange={onChange} placeholder="e.g. 373" type="number" error={errors.engine_cc} />
              <RadioGroup label="Fuel Type" name="fuel_type" value={form.fuel_type} onChange={onChange} options={["Petrol", "Electric", "Other"]} />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <TextInput label="Registration Number" name="registration_number" value={form.registration_number} onChange={onChange} required error={errors.registration_number} placeholder="e.g. UP32 AB 1234" />
              <SelectInput label="Registration State" name="registration_state" value={form.registration_state} onChange={onChange} options={INDIAN_STATES} required error={errors.registration_state} placeholder="Select state" />
              <SelectInput label="Registration City" name="registration_city" value={form.registration_city} onChange={onChange} options={citiesForState(form.registration_state)} required error={errors.registration_city} disabled={!form.registration_state} placeholder={form.registration_state ? "Select city" : "Select state first"} />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <SelectInput label="Year of Registration" name="registration_year" value={form.registration_year} onChange={onChange} options={YEARS} required error={errors.registration_year} placeholder="Select year" />
              <TextInput label="KMs Driven" name="kms_driven" value={form.kms_driven} onChange={onChange} required error={errors.kms_driven} placeholder="e.g. 12000" type="number" />
              <SelectInput label="Previous Owners" name="previous_owners" value={form.previous_owners} onChange={onChange} options={["1", "2", "3", "4"]} required />
            </div>
            <RadioGroup label="Vehicle Condition" name="condition" value={form.condition} onChange={onChange} options={["Showroom Condition", "Very Good", "Good", "Average", "Needs Work"]} required />
            <div className="grid sm:grid-cols-2 gap-5">
              <ToggleBool label="Running Loan on vehicle?" name="has_loan" value={form.has_loan} onChange={onChange} />
              <ToggleBool label="Pending Challan?" name="has_challan" value={form.has_challan} onChange={onChange} />
            </div>
            <ToggleBool label="Any major accident history?" name="accident_history" value={form.accident_history} onChange={onChange} />
            <TextArea label="Modifications / Accessories" name="modifications" value={form.modifications} onChange={onChange} placeholder="e.g. Akrapovič exhaust, crash guards, saddlebags" rows={3} maxLength={600} />
          </div>
        </section>

        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Price + BB Valuation */}
        <section>
          <SectionHeader n="03" icon={Gauge} title="Pricing" />
          <div className="space-y-5">
            <TextInput label="Expected Selling Price (₹)" name="expected_price" value={form.expected_price} onChange={onChange} required error={errors.expected_price} placeholder="e.g. 280000" type="number" />
            <div className="p-5 rounded-xl border border-[#00cccc]/25 bg-[#00cccc]/[0.04]">
              <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] uppercase text-[#00cccc] mb-2">
                {valuating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
                // BB Smart Valuation
              </div>
              {valuation ? (
                <>
                  <div className="text-2xl sm:text-3xl font-bold font-mono text-white">
                    {INR(valuation.low)} <span className="text-zinc-500 text-base font-sans">–</span> {INR(valuation.high)}
                  </div>
                  <div className="mt-1 text-xs text-zinc-400">BB Estimated Market Value &middot; Mid: {INR(valuation.estimate)} &middot; Segment: {valuation.segment}</div>
                  <p className="mt-3 text-xs text-zinc-500 leading-relaxed">Our estimate reflects current market signals. Your asking price is independent — buyers will see both.</p>
                </>
              ) : (
                <div className="text-sm text-zinc-400">Fill in brand, model, year and KMs to see your BB valuation.</div>
              )}
            </div>
            <TextArea label="Anything else buyers should know? (optional)" name="additional_notes" value={form.additional_notes} onChange={onChange} placeholder="Service history, recent repairs, reason for sale, etc." rows={4} maxLength={2000} />
          </div>
        </section>

        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Photos */}
        <section>
          <SectionHeader n="04" icon={Camera} title="Upload Bike Photos" />
          <p className="text-sm text-zinc-400 mb-5">Upload up to 5 clear photos (front, rear, sides, odometer, any damage). Pick a cover photo — it&apos;ll show as the main image on your listing.</p>
          {photos.length < 5 && (
            <label htmlFor="photo-input" data-testid="photo-dropzone"
              className="block cursor-pointer rounded-2xl border border-dashed border-[#00cccc]/30 bg-[#00cccc]/[0.03] hover:bg-[#00cccc]/[0.06] hover:border-[#00cccc]/60 transition-all p-8 text-center">
              <Upload className="w-6 h-6 text-[#00cccc] mx-auto mb-2" />
              <div className="text-sm text-white font-semibold">Drop photos or click to upload</div>
              <div className="text-xs text-zinc-400 mt-1">JPG / PNG &middot; Up to 5 files &middot; 5 MB each</div>
              <input id="photo-input" type="file" multiple accept="image/*"
                onChange={(e) => onPhotoFiles(e.target.files)} className="hidden" data-testid="photo-input" />
            </label>
          )}
          {errors._photos && <p className="mt-2 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors._photos}</p>}
          {photos.length > 0 && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {photos.map((p, i) => (
                <div key={i} className={`relative rounded-xl overflow-hidden border transition-all ${primaryIdx === i ? "border-[#00cccc] ring-2 ring-[#00cccc]/40" : "border-white/10"}`} data-testid={`photo-tile-${i}`}>
                  <div className="aspect-square bg-[#15181C]">
                    <img src={p.preview} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-[#00cccc] animate-spin" />
                    </div>
                  )}
                  {p.error && (
                    <div className="absolute inset-0 bg-red-900/70 flex items-center justify-center text-xs text-white p-2 text-center">{p.error}</div>
                  )}
                  <div className="absolute top-1 left-1 flex gap-1">
                    {primaryIdx === i ? (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-mono bg-[#00cccc] text-black font-semibold" data-testid={`primary-badge-${i}`}>COVER</span>
                    ) : (
                      <button type="button" onClick={() => setPrimaryIdx(i)}
                        className="px-2 py-0.5 rounded-full text-[9px] font-mono bg-black/60 text-white hover:bg-[#00cccc] hover:text-black transition-all"
                        data-testid={`set-primary-${i}`}>Set Cover</button>
                    )}
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1">
                    <button type="button" onClick={() => removePhoto(i)}
                      className="w-6 h-6 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                  <div className="absolute bottom-1 left-1 flex gap-1">
                    <button type="button" onClick={() => movePhoto(i, -1)} className="w-6 h-6 rounded-full bg-black/60 hover:bg-black text-white text-xs" aria-label="Move left">←</button>
                    <button type="button" onClick={() => movePhoto(i, 1)} className="w-6 h-6 rounded-full bg-black/60 hover:bg-black text-white text-xs" aria-label="Move right">→</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="pt-4 space-y-4">
          {submitError && (
            <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-sm text-red-300 flex items-start gap-2" data-testid="submit-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{submitError}</span>
            </div>
          )}
          <button type="button" onClick={onPreview} data-testid="preview-button"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all">
            Preview My Listing <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-zinc-500">We&apos;ll show you a preview before publishing to the marketplace.</p>
        </div>
      </div>
    </>
  );
}

// =========================
// Preview screen
// =========================
function PreviewScreen({ form, photos, primaryUrl, valuation, onEdit, onPublish, submitting, submitError }) {
  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Listing Preview</span>
          <h2 className="mt-1 text-xl sm:text-2xl font-bold">This is how buyers will see your bike</h2>
        </div>
        <button onClick={onEdit} data-testid="edit-back-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white hover:border-[#00cccc]/50 hover:text-[#00cccc] transition-all text-sm">
          <Edit className="w-4 h-4" /> Edit
        </button>
      </div>

      <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: "#15181C" }}>
        <div className="aspect-[16/9] bg-[#1A1E23] relative">
          {primaryUrl ? <img src={primaryUrl} alt="Main" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-600">No photo</div>}
          <div className="absolute top-3 left-3 flex gap-2">
            <span className="px-3 py-1 rounded-full text-[10px] font-mono bg-black/70 text-white border border-white/20">PREVIEW</span>
          </div>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">{form.registration_year} &middot; {form.brand}</div>
              <h3 className="mt-0.5 text-xl sm:text-2xl font-bold text-white">{form.brand} {form.model} {form.variant}</h3>
              <div className="mt-1.5 text-xs text-zinc-400 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {form.seller_city}, {form.seller_state}</div>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">Seller Asking</div>
              <div className="text-2xl font-bold text-[#00cccc]">{INR(parseInt(form.expected_price) || 0)}</div>
              {valuation && <div className="mt-1 text-[11px] text-zinc-400">BB Estimate: {INR(valuation.low)} – {INR(valuation.high)}</div>}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-white/10">
            {[
              ["KMs", form.kms_driven],
              ["Owners", form.previous_owners],
              ["CC", form.engine_cc || "—"],
              ["Fuel", form.fuel_type],
              ["Reg No.", form.registration_number],
              ["Reg City", form.registration_city],
              ["Condition", form.condition],
              ["Accident", form.accident_history ? "Yes" : "No"],
            ].map(([k, v]) => (
              <div key={k} className="p-2 rounded-lg bg-white/[0.04] border border-white/10">
                <div className="text-[11px] font-sans font-semibold uppercase tracking-wider text-zinc-300">{k}</div>
                <div className="text-[13px] text-white mt-0.5 truncate">{v || "—"}</div>
              </div>
            ))}
          </div>

          {form.modifications && (
            <div><div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#00cccc] mb-1">// Modifications</div><p className="text-[13px] text-zinc-300">{form.modifications}</p></div>
          )}
          {form.additional_notes && (
            <div><div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#00cccc] mb-1">// Notes</div><p className="text-[13px] text-zinc-300">{form.additional_notes}</p></div>
          )}

          {photos.length > 1 && (
            <div className="pt-3 border-t border-white/10">
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500 mb-2">// Gallery</div>
              <div className="grid grid-cols-5 gap-1.5">
                {photos.map((p, i) => (
                  <div key={i} className="aspect-square rounded-md overflow-hidden border border-white/10 bg-[#15181C]">
                    <img src={p.preview} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {submitError && (
        <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-sm text-red-300 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{submitError}</span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button onClick={onPublish} disabled={submitting} data-testid="publish-button"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all disabled:opacity-70">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : <>Publish Listing <ArrowRight className="w-4 h-4" /></>}
        </button>
        <button onClick={onEdit} className="w-full sm:w-auto px-6 py-3 rounded-full border border-white/20 text-white hover:border-[#00cccc]/50 transition-all text-sm">
          Keep Editing
        </button>
      </div>
    </div>
  );
}

// =========================
// Success screen
// =========================
function SuccessScreen({ listingId, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="min-h-[70vh] flex flex-col items-center justify-center text-center" data-testid="success-screen">
      <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 12, stiffness: 200 }} className="relative w-28 h-28 mb-8">
        <div className="absolute inset-0 rounded-full bg-[#00cccc]/10 border border-[#00cccc]/30 animate-pulse-glow" style={{ boxShadow: "0 0 48px rgba(0,204,204,0.35)" }} />
        <div className="absolute inset-3 rounded-full bg-[#00cccc]/20 border border-[#00cccc]/60 flex items-center justify-center">
          <Check className="w-12 h-12 text-[#00cccc]" strokeWidth={3} />
        </div>
      </motion.div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
        Your Bike Is <span className="text-gradient-cyan">Live.</span>
      </h2>
      <p className="mt-4 text-zinc-400 max-w-xl">Your listing is now visible on the Biker Boulevard marketplace. Serious riders can discover it instantly.</p>
      <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
        <button onClick={() => navigate(`/marketplace/${listingId}`)} data-testid="view-listing" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all">
          View My Listing <ArrowRight className="w-4 h-4" />
        </button>
        <Link to="/marketplace" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/20 text-white hover:border-[#00cccc]/50 transition-all">
          Explore Marketplace
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/20 text-white hover:border-[#00cccc]/50 transition-all">
          <Home className="w-4 h-4" /> Home
        </Link>
      </div>
      <div className="mt-8 flex items-center gap-2 text-sm text-zinc-500">
        <Instagram className="w-4 h-4" />
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#00cccc]">Follow Biker Boulevard for drops & rider stories</a>
      </div>
    </motion.div>
  );
}
